import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pill, ChevronRight, Plus, Loader2 } from 'lucide-react';
import { listMedications, seedDemoMedications } from '@/lib/medications';
import { consumePostAddToast, readPendingSevere, pairKey } from '@/lib/interactions';
import { SeverityColors } from '@/lib/theme';

export default function MedicationsPage() {
  const navigate = useNavigate();
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [severeBanner, setSevereBanner] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try { setMeds(await listMedications()); } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const pending = consumePostAddToast();
    if (pending) setToast(pending);
    const sev = readPendingSevere();
    setSevereBanner(sev);
    setBannerDismissed(false);
  }, [load]);

  const onSeed = async () => {
    setSeeding(true);
    setError(null);
    try { setMeds(await seedDemoMedications()); } catch (e) { setError(e.message); } finally { setSeeding(false); }
  };

  const toastColors = toast ? (SeverityColors[toast.kind] || SeverityColors.uncertain) : null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-24 relative">
      <h1 className="text-3xl font-extrabold" style={{ color: '#5B2D1F' }} data-testid="medications-screen-title">Medications</h1>
      <p className="text-base mt-0.5 mb-4" style={{ color: '#4B6B79' }}>Your saved medications, sorted by name.</p>

      {/* Severe banner */}
      {severeBanner && !bannerDismissed && (
        <div data-testid="medications-severe-banner" className="p-4 rounded-xl border mb-4 flex items-start gap-3" style={{ backgroundColor: '#FCE7EA', borderColor: '#B0303A' }}>
          <div className="flex-1">
            <p className="font-extrabold" style={{ color: '#B0303A' }}>Severe interaction: {severeBanner.newDrug} × {severeBanner.otherDrug}</p>
            <p className="text-sm mt-1" style={{ color: '#B0303A' }}>{severeBanner.summary}</p>
            <button onClick={() => navigate(`/interactions/${pairKey(severeBanner.newDrug, severeBanner.otherDrug)}?drugA=${severeBanner.newDrug}&drugB=${severeBanner.otherDrug}`)} className="text-sm font-bold mt-2 underline" style={{ color: '#B0303A' }}>View details</button>
          </div>
          <button onClick={() => setBannerDismissed(true)} className="text-lg font-bold" style={{ color: '#B0303A' }}>×</button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div data-testid="medications-toast" className="p-3 rounded-xl border mb-4 flex items-center gap-2" style={{ backgroundColor: toastColors?.bg, borderColor: toastColors?.border }}>
          <span className="text-sm font-bold" style={{ color: toastColors?.text }}>{toast.kind === 'unverified' ? 'Unverified' : toast.kind}: {toast.summary}</span>
          <button onClick={() => setToast(null)} className="ml-auto font-bold" style={{ color: toastColors?.text }}>×</button>
        </div>
      )}

      {error && <div data-testid="medications-error" className="p-4 rounded-xl border mb-4" style={{ backgroundColor: '#FCE7EA', borderColor: '#B0303A' }}><p className="font-semibold" style={{ color: '#B0303A' }}>{error}</p></div>}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <img src="/brand/logo-round.png" alt="" className="w-14 h-14 rounded-full opacity-35" />
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#D77A52' }} />
        </div>
      ) : meds.length === 0 ? (
        <div data-testid="medications-empty" className="rounded-2xl border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
          <img src="/brand/logo-round.png" alt="" className="w-14 h-14 rounded-full opacity-35 mb-4" />
          <h2 className="text-xl font-extrabold mb-1" style={{ color: '#0F2A35' }}>No medications yet</h2>
          <p className="text-base mb-4" style={{ color: '#4B6B79' }}>Tap the + button to add your first medication. Or load a few example medications to explore the app.</p>
          <button onClick={onSeed} disabled={seeding} data-testid="medications-seed-btn" className="px-6 py-3 rounded-xl border-2 font-bold transition-opacity hover:opacity-90 disabled:opacity-50" style={{ borderColor: '#D77A52', color: '#D77A52' }}>
            {seeding ? 'Seeding…' : 'Seed demo data'}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {meds.map((m) => (
            <button key={m.id} onClick={() => navigate(`/medications/${m.id}`)} data-testid={`med-row-${m.name.toLowerCase()}`} className="w-full flex items-center gap-4 p-4 rounded-xl border text-left transition-opacity hover:opacity-90" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#EEF4F7' }}>
                <Pill className="h-5 w-5" style={{ color: '#5B2D1F' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold truncate" style={{ color: '#0F2A35' }}>{m.name}</span>
                  {m.unverified && <span className="text-[11px] font-bold uppercase px-2 py-0.5 rounded-full border" style={{ backgroundColor: '#FFF4DF', borderColor: '#B07A1F', color: '#B07A1F' }}>Unverified</span>}
                </div>
                <p className="text-sm mt-0.5" style={{ color: '#4B6B79' }}>{m.dosage} · {m.frequency}</p>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0" style={{ color: '#4B6B79' }} />
            </button>
          ))}
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => navigate('/medications/add')}
        data-testid="medications-add-fab"
        className="fixed bottom-8 right-8 md:absolute md:bottom-6 md:right-6 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#D77A52' }}
      >
        <Plus className="h-7 w-7" />
      </button>
    </div>
  );
}
